import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageSelector from "../components/PageSelector";
import usePagedCommunity from "../hooks/usePagedCommunity";
import CommunityType from "../interfaces/community";
import useOrdinationStore from "../store/ordinationStore";
import useUserStore from "../store/userStore";
import { ASSETS_URL } from "../util/constants";
import "./Community.css";

const getCommunityLogo = (community: CommunityType) => {
  const logoPath = community.logo || community.pathImage;

  if (!logoPath) {
    return "/logo.png";
  }

  if (logoPath.startsWith("http")) {
    return logoPath;
  }

  return `${ASSETS_URL}${logoPath.startsWith("/") ? logoPath : `/${logoPath}`}`;
};

const Community = () => {
  const navigate = useNavigate();
  const page = useOrdinationStore((state) => state.page);
  const pageSize = useOrdinationStore((state) => state.pageSize);
  const name = useOrdinationStore((state) => state.name);
  const fieldOrder = useOrdinationStore((state) => state.fieldOrder);
  const currentOrder = useOrdinationStore((state) => state.currentOrder);
  const resetOrdination = useOrdinationStore((state) => state.resetOrdination);
  const token = useUserStore((state) => state.token);
  const setDestinyRoute = useUserStore((state) => state.setDestinyRoute);

  useEffect(() => {
    resetOrdination();
  }, [resetOrdination]);

  const { data, isLoading, error } = usePagedCommunity({
    page,
    pageSize,
    name,
    fieldOrder,
    currentOrder,
  });

  if (isLoading) {
    return <div className="community-page-container">Loading communities...</div>;
  }

  if (error) {
    return (
      <div className="community-page-container">
        <div className="community-empty-state">
          Error loading communities.
        </div>
      </div>
    );
  }

  const communities = data?.items ?? [];

  const openCreateCommunity = () => {
    if (!token) {
      setDestinyRoute("/community");
      navigate("/login");
      return;
    }
    navigate("/community/create");
  };

  return (
    <div className="community-page-container">
      <>
        <div className="community-list-header">
          <h2>Community</h2>
          <button
            className="community-create-button"
            type="button"
            onClick={openCreateCommunity}
          >
            Create Community
          </button>
        </div>

        {communities.length === 0 ? (
          <div className="community-empty-state">
            There are no communities to display yet.
          </div>
        ) : (
          <div className="community-grid">
            {communities.map((community) => (
              <article
                className="community-card"
                key={community.id ?? community.name}
                role="button"
                tabIndex={0}
                onClick={() =>
                  community.id && navigate(`/community/${community.id}`)
                }
                onKeyDown={(event) => {
                  if (
                    community.id &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    navigate(`/community/${community.id}`);
                  }
                }}
              >
                <div className="community-logo-frame">
                  <img
                    src={getCommunityLogo(community)}
                    alt={community.name}
                    className="community-logo"
                  />
                </div>

                <div className="community-card-content">
                  <div>
                    <h3>{community.name}</h3>
                    <p>
                      {community.description || "No description provided."}
                    </p>
                  </div>

                  <div className="community-meta">
                    <span>Owner: {community.owner?.username ?? "Unknown"}</span>
                    <span>{community.members?.length ?? 0} members</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {data && <PageSelector {...data} />}
      </>
    </div>
  );
};

export default Community;
